import { isObject } from '@utmost.co/utmost-design-system';
import * as yup from 'yup';
import { type ObjectSchemaDefinition } from 'yup';
import { logger } from '../../../logger';
import { ElementWithMeta, FieldValidation, ValidationDefinition, ValidationType } from '../../../types';
import { InputModel } from '../model';
import { getModelPathFromIndex } from '../model/getModelPath';
import { objectUtil } from '../objectUtil';
import { buildEntitySchema, isEntity } from './builders/buildEntitySchema';

export function schemaBuilderV2<T extends object = object>(elements: ElementWithMeta<T>[]): yup.ObjectSchema<T> {
  const rootSchema = yup.object().label('smartform-root-level');
  return mapToObjectSchema(buildValidationDefinition(elements), rootSchema, elements) as yup.ObjectSchema<T>;
}

/**
 * Generates a validation definition based on the provided elements and their respective metadata.
 *
 * @param {ElementWithMeta<Model>[]} elements - Array of elements containing metadata and field definitions.
 * @return {ValidationDefinition<Type>} A comprehensive validation definition constructed from the given elements.
 */

export function buildValidationDefinition<Type extends string, Model = InputModel>(
  elements: ElementWithMeta<Model>[]
): ValidationDefinition<Type> {
  return elements.reduce((acc, element) => {
    const validations: (FieldValidation | undefined)[] = element.meta.map((m) => m.validation) ?? [];
    element.fields.forEach((field, index) => {
      const targetPath = getModelPathFromIndex(element, index);
      const nextPath = targetPath.replaceAll(/\./g, '.fields.');
      const existingFields = objectUtil.getIn(acc, nextPath)?.fields;

      // If validation already exists, extend it instead of replacing it.
      // This can happen when a Smart Form includes a custom field element for path: primaryEngagement.customFields.someID,
      // but there is another element that validates primaryEngagement.customFields path.
      // In this case we check values against both validators.

      if (existingFields && nextPath) {
        acc = objectUtil.setIn(acc, nextPath, {
          fields: existingFields,
          validation: {
            elementType: element.type,
            fieldIndex: index,
            userValidation: field.validation ?? {},
            errorMessages: element.errorMessages,
            builtin: validations?.[index]?.builtin ?? ((schema) => schema),
            subtype: validations?.[index]?.subtype,
            subTypeSchema: validations?.[index]?.subTypeSchema,
            type: validations?.[index]?.type ?? (!isEntity(existingFields) ? 'object' : undefined) // if type does not exist - defaulting to 'object' due to presence of validation
          }
        });
      } else if (nextPath) {
        acc = objectUtil.setIn(acc, nextPath, {
          validation: {
            elementType: element.type,
            fieldIndex: index,
            userValidation: field.validation,
            errorMessages: element.errorMessages,
            ...validations?.[index]
          }
        });
      }
    });
    return acc;
  }, {});
}

function elementWithMetaToEntry(element: ElementWithMeta<any>): string {
  let content = 'no meta validation';
  try {
    if (element) {
      content = JSON.stringify(element, null, 2);
    }
  } catch (e) {}

  return `${element.type}\n${content}\n────────────────────────`;
}
function formatElementsForLogging(elements: ElementWithMeta<any>[]): string {
  return elements.map(elementWithMetaToEntry).join('\n');
}

/**
 * Maps a given validation definition object to a Yup object schema.
 * This method processes each key in the input validation definition and builds
 * corresponding schema definitions based on validation or nested fields.
 *
 * @param {ValidationDefinition<any>} obj - The validation definition object containing keys and their associated validation and field configurations.
 * @param {yup.ObjectSchema} schema - An initial Yup object schema that serves as the base to apply the mapped schema transformations.
 * @param {ElementWithMeta[]} elements - Elements metadata used for logging purposes when schema configurations do not match expected types.
 * @return {yup.ObjectSchema} - The final Yup object schema after applying the mappings from the validation definition.
 */
export function mapToObjectSchema(
  obj: ValidationDefinition<any>,
  schema: yup.ObjectSchema,
  elements: ElementWithMeta<any>[]
): yup.ObjectSchema {
  for (const key in obj) {
    const { validation, fields } = obj[key] ?? {};

    const hasNestedFields = isObject(fields) && Object.keys(fields ?? {}).length > 0;

    // If validation has nested fields but no explicit type, default to 'object' to support nesting.
    // Without this, undefined type falls through to 'string' in buildEntitySchema,
    // which prevents attaching nested field schemas.
    const effectiveValidation =
      validation && hasNestedFields && !validation.type ? { ...validation, type: ValidationType.OBJECT } : validation;

    let nextSchema = effectiveValidation ? buildEntitySchema(effectiveValidation, obj) : yup.object().default({});

    nextSchema.label(effectiveValidation ? `${key}-${effectiveValidation.type}-defined` : `${key}-object-default`);
    // if we have a validation for sub fields - we need to add them to the schema
    if (hasNestedFields) {
      if (nextSchema.isType(yup.object) && 'shape' in nextSchema) {
        nextSchema = nextSchema.shape(
          mapToObjectSchema(fields, nextSchema, elements).fields as ObjectSchemaDefinition<any, any>
        );
      } else {
        logger.error(
          `The Smart Form Schema for ${key} is not an object type, but the configuration requires it to be one. Skipping nested fields (${Object.keys(fields).join(', ')}). Please check the validation configuration for this field.`,
          formatElementsForLogging(elements),
          schema,
          nextSchema
        );
      }
    }

    schema = schema.shape({
      [key]: nextSchema
    });
  }
  return schema;
}
